import {
  Lci3SalesMetrics,
  Lci3Report,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { Grid, Typography } from '@mui/material';
import { WaterfallChartWithHeader } from '../Charts/WaterfallChartWithHeader';
import { channelNames } from './SalesMetrics';
import { clampNegativeToZero } from '@utils/valueTransformers';
import { ValueInput, formatters } from '@utils/formattersV2';
import { WaterfallChartProps } from '@features/campaigns/subdomains/visitation/configuration/waterfallChartOptions';
import { ColumnChartWithHeader } from '../Charts/ColumnChartWithHeader';
import { ColumnChartProps } from '@features/campaigns/subdomains/visitation/configuration/columnChartOptions';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { useLciAIInsights } from '@features/ai-insights';

type Lci3SalesMetric = keyof Lci3SalesMetrics;
type ChannelName = keyof typeof channelNames;
type LiftCategory = (typeof liftCategories)[number];

const liftCategories = [
  'transactionSizeLift',
  'transactionRateLift',
  'penetrationLift',
  'incrementalSalesLift',
] as const satisfies readonly Lci3SalesMetric[];

const salesCategories = [
  'transactionSizeSales',
  'transactionRateSales',
  'penetrationSales',
  'incrementalSales',
] as const satisfies readonly Lci3SalesMetric[];

const categoryToName: Record<LiftCategory, string> = {
  transactionSizeLift: 'Transaction Size',
  transactionRateLift: 'Transaction Rate',
  penetrationLift: 'Penetration',
  incrementalSalesLift: 'Incrementality',
};

const liftCategoriesToWaterfallData = (
  channel: ChannelName,
  salesMetrics: Lci3SalesMetrics
): WaterfallChartProps['data'] =>
  liftCategories.map((category, index) => {
    const value = salesMetrics[category][channel] ?? 0;
    return {
      name: categoryToName[category],
      value: salesMetrics[salesCategories[index]][channel] ?? 0,
      dataLabelValue:
        index === liftCategories.length - 1
          ? clampNegativeToZero(value)
          : value,
    };
  });

export const liftCategoriesToWaterfallChartProps = (
  channel: ChannelName,
  salesMetrics: Lci3SalesMetrics
): WaterfallChartProps => {
  return {
    data: liftCategoriesToWaterfallData(channel, salesMetrics),
    valueFormatter: waterfallValueFormatter,
    yAxisTitle: 'Incremental Sales',
    yAxisFormatter: waterfallYAxisFormatter,
  };
};

const fieldToColumnChartData = (
  field: Lci3SalesMetric,
  salesMetrics: Lci3SalesMetrics
): ColumnChartProps['data'] =>
  (Object.keys(channelNames) as ChannelName[]).map(channel => ({
    name: channelNames[channel],
    value: salesMetrics[field][channel] ?? 0,
  }));

const waterfallValueFormatter = (value: ValueInput) =>
  formatters.percentage(value, { decimals: 1 });
const waterfallYAxisFormatter = formatters.currency;

type ContributionMetricsProps = {
  salesMetrics: Lci3SalesMetrics;
  reports: Lci3Report[];
  highlightsComponent: React.ReactNode;
};

export const ContributionMetrics = (props: ContributionMetricsProps) => {
  const latestReportWithSalesMetrics = props.reports.findLast(
    report => report.salesMetrics
  );

  const { rsaAiInsights } = useLciAIInsights(
    latestReportWithSalesMetrics ?? null,
    'contribution-metrics'
  );

  return (
    <Grid container spacing="1.5rem">
      <Grid size={12}>
        <AIInsightsCard
          testId="lci-contribution-metrics-ai-insights"
          title="Contribution Metrics"
          aiInsights={rsaAiInsights ?? undefined}
          highlights={props.highlightsComponent}
        />
      </Grid>
      <Grid size={12}>
        <Typography fontStyle="italic" textAlign="right">
          Metrics are not additive due to interaction effects
        </Typography>
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <WaterfallChartWithHeader
          title={channelNames.total}
          value={clampNegativeToZero(
            props.salesMetrics.incrementalSalesLift.total ?? 0
          )}
          valueFormatter={formatters.lift}
          confidenceLevel={props.salesMetrics.confidenceLevel.total}
          chartProps={liftCategoriesToWaterfallChartProps(
            'total',
            props.salesMetrics
          )}
          data-testid="total-waterfall-chart"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <WaterfallChartWithHeader
          title={channelNames.inStore}
          value={clampNegativeToZero(
            props.salesMetrics.incrementalSalesLift.inStore ?? 0
          )}
          valueFormatter={formatters.lift}
          confidenceLevel={props.salesMetrics.confidenceLevel.inStore}
          chartProps={liftCategoriesToWaterfallChartProps(
            'inStore',
            props.salesMetrics
          )}
          data-testid="in-store-waterfall-chart"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <WaterfallChartWithHeader
          title={channelNames.online}
          value={clampNegativeToZero(
            props.salesMetrics.incrementalSalesLift.online ?? 0
          )}
          valueFormatter={formatters.lift}
          confidenceLevel={props.salesMetrics.confidenceLevel.online}
          chartProps={liftCategoriesToWaterfallChartProps(
            'online',
            props.salesMetrics
          )}
          data-testid="online-waterfall-chart"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <ColumnChartWithHeader
          title={categoryToName.transactionSizeLift}
          chartProps={{
            yAxisValueFormatter: formatters.currency,
            valueFormatter: value =>
              formatters.currency(value, { decimals: 2 }),
            data: fieldToColumnChartData(
              'exposedTransactionSize',
              props.salesMetrics
            ),
          }}
          data-testid="exposed-transaction-size-chart"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <ColumnChartWithHeader
          title={categoryToName.transactionRateLift}
          chartProps={{
            yAxisValueFormatter: formatters.number,
            yAxisMinTickInterval: 1,
            valueFormatter: value => formatters.number(value, { decimals: 1 }),
            data: fieldToColumnChartData(
              'exposedTransactionRate',
              props.salesMetrics
            ),
          }}
          data-testid="exposed-transaction-rate-chart"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <ColumnChartWithHeader
          title={categoryToName.penetrationLift}
          chartProps={{
            yAxisValueFormatter: formatters.percentage,
            yAxisMinTickInterval: 0.01,
            valueFormatter: value =>
              formatters.percentage(value, { decimals: 1 }),
            data: fieldToColumnChartData(
              'exposedPenetration',
              props.salesMetrics
            ),
          }}
          data-testid="exposed-penetration-chart"
        />
      </Grid>
    </Grid>
  );
};
